import React from "react";
import PropTypes from "prop-types";
import CountryPicker from "./CountryPicker";
import RadioQuestion from "./RadioQuestion";

/**
 * Component for capturing fund location information with conditional questions.
 *
 * Flow:
 * 1. Always ask: "Where are these funds currently held?" (country picker)
 * 2. If UK selected: "Have these funds been held outside the UK in the past 12 months?" (yes/no)
 * 3. If yes: "Where were these funds held before?" (country picker)
 * 4. If yes: "When were the funds moved to the UK?" (radio with 3 time periods)
 *
 * Value shape:
 * {
 *   currentCountry: "GBR",              // ISO alpha-3 (always present)
 *   hasBeenOutsideUK: true,            // boolean (only if currentCountry === 'GBR')
 *   previousCountry: "USA",             // ISO alpha-3 (only if hasBeenOutsideUK === true)
 *   whenMoved: "within_6_months"       // enum (only if hasBeenOutsideUK === true)
 * }
 */
function FundLocationQuestions({ value, onChange, validate, errors }) {
  const currentCountry = value?.currentCountry || "";
  const isUKBased = currentCountry === "GBR";
  const hasBeenOutsideUK = value?.hasBeenOutsideUK;
  const previousCountry = value?.previousCountry || "";
  const whenMoved = value?.whenMoved || "";

  const validateField = (fieldName) => (event) => validate?.(fieldName, event);

  const hasBeenOutsideUKSelection = hasBeenOutsideUK === true ? "yes" : hasBeenOutsideUK === false ? "no" : "";

  const handleCurrentCountryChange = (countryCode) => {
    const selectedUK = countryCode === "GBR";
    const wasAlreadyUK = value?.currentCountry === "GBR";

    // Already on UK, nothing to change
    if (selectedUK && wasAlreadyUK) return;

    // Clear dependent fields: either selecting non-UK, or fresh UK selection
    onChange({
      currentCountry: countryCode,
      hasBeenOutsideUK: null,
      previousCountry: null,
      whenMoved: null,
    });
  };

  const handleHasBeenOutsideUKChange = (selectedValue) => {
    const selectedYes = selectedValue === "yes";
    const wasAlreadyYes = value?.hasBeenOutsideUK === true;

    // Already on Yes, nothing to change
    if (selectedYes && wasAlreadyYes) return;

    // Clear dependent fields: either selecting No, or fresh Yes selection
    onChange({
      currentCountry: value?.currentCountry,
      hasBeenOutsideUK: selectedYes,
      previousCountry: null,
      whenMoved: null,
    });
  };

  const handlePreviousCountryChange = (countryCode) => {
    onChange({
      ...value,
      previousCountry: countryCode,
    });
  };

  const handleWhenMovedChange = (selectedValue) => {
    onChange({
      ...value,
      whenMoved: selectedValue,
    });
  };

  return (
    <div className="fund-location-questions">
      <CountryPicker
        question="Where are these funds currently held?"
        value={currentCountry}
        onChange={handleCurrentCountryChange}
        validate={validateField("currentCountry")}
        validationError={errors?.currentCountry}
      />

      {isUKBased && (
        <>
          <RadioQuestion
            question="Have these funds been held outside the UK in the past 12 months?"
            selected={hasBeenOutsideUKSelection}
            optionSelected={handleHasBeenOutsideUKChange}
            validate={validateField("hasBeenOutsideUK")}
            validationError={errors?.hasBeenOutsideUK}
            options={[
              { value: "yes", label: "Yes" },
              { value: "no", label: "No" },
            ]}
          />

          {hasBeenOutsideUK === true && (
            <>
              <CountryPicker
                question="Where were these funds held before?"
                value={previousCountry}
                onChange={handlePreviousCountryChange}
                validate={validateField("previousCountry")}
                validationError={errors?.previousCountry}
              />

              <RadioQuestion
                question="When were the funds moved to the UK?"
                selected={whenMoved}
                optionSelected={handleWhenMovedChange}
                validate={validateField("whenMoved")}
                validationError={errors?.whenMoved}
                options={[
                  { value: "within_6_months", label: "Within last 6 months" },
                  { value: "within_year", label: "Within last year" },
                  { value: "over_year", label: "More than a year ago" },
                ]}
              />
            </>
          )}
        </>
      )}
    </div>
  );
}

FundLocationQuestions.propTypes = {
  /**
   * Current value of the fund location data
   * Shape: { currentCountry, hasBeenOutsideUK, previousCountry, whenMoved }
   */
  value: PropTypes.shape({
    currentCountry: PropTypes.string,
    hasBeenOutsideUK: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf([null])]),
    previousCountry: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
    whenMoved: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
  }),
  /**
   * Callback when any value changes - receives the complete location object
   */
  onChange: PropTypes.func.isRequired,
  /**
   * Validation callback - receives field name and event
   */
  validate: PropTypes.func,
  /**
   * Validation errors for individual fields
   * Shape: { currentCountry, hasBeenOutsideUK, previousCountry, whenMoved }
   */
  errors: PropTypes.shape({
    currentCountry: PropTypes.string,
    hasBeenOutsideUK: PropTypes.string,
    previousCountry: PropTypes.string,
    whenMoved: PropTypes.string,
  }),
};

export default FundLocationQuestions;
